import { ScheduledMaintenance, SaveScheduledMaintenanceRequest, DeleteScheduledMaintenanceRequest, CompleteScheduledMaintenanceRequest, ForceStartOfScheduledMaintenanceRequest } from "../../../../models/basics/scheduled-maintenance.models";
import { autoinject, computedFrom, BindingEngine } from "aurelia-framework";
import { SourceType, DataType, DataGridColumn, ItemSource } from "../../../../models/queries/queries.models";
import { ScheduledMaintenanceService } from "../../../../services/basics/scheduled-maintenance.service";
import { I18N } from "aurelia-i18n";
import * as moment from "moment";
import { GenericConfirmationDialogModel } from "../../../../models/basics/generic-confirmation-dialog-model";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { BaseClaims } from "../../../../constants/authentication/base-claims";
import { UnitOfWorkObserver } from "../../../../utilities/unit-of-work-observer";
import { Reflection } from "../../../../utilities/reflection";

@autoinject()
export class ScheduledMaintenancesView {

    public canAdd: boolean;
    public canUpdate: boolean;
    public canDelete: boolean;
    public canComplete: boolean;
    public scheduledMaintenanceInEdit: ScheduledMaintenance;
    public scheduledMaintenanceUow: UnitOfWorkObserver;
    public initialSorting: DataGridColumn[] = [];
    public columns: DataGridColumn[] = [];
    public itemSource: ItemSource;

    public constructor (private i18N: I18N, 
        private scheduledMaintenanceService: ScheduledMaintenanceService,
        private userClaims: UserClaimsService,
        private dialogClientService: DialogClientService,
        private notificationService: NotificationClientService,
        private engine: BindingEngine) {
        this.canAdd = this.userClaims.hasPermission(BaseClaims.AddScheduledMaintenances);
        this.canUpdate = this.userClaims.hasPermission(BaseClaims.UpdateScheduledMaintenances);
        this.canDelete = this.userClaims.hasPermission(BaseClaims.DeleteScheduledMaintenances);
        this.canComplete = this.userClaims.hasPermission(BaseClaims.CompleteScheduledMaintenances);
        this.setColumns();
        this.initialSorting = this.getSorting();
    }
    
    protected get odataUrl(): string {
        return "odata/ScheduledMaintenances";
    }

    @computedFrom(Reflection.getPropertyPath<ScheduledMaintenancesView>(x => x.scheduledMaintenanceInEdit))
    public get isUpcoming() {
        if(!this.scheduledMaintenanceInEdit || !this.scheduledMaintenanceInEdit.scheduledMaintenanceId)
            return false;

        const now = new Date(moment().format('DD MMM YYYY HH:mm'));
        const startDate = new Date(moment(this.scheduledMaintenanceInEdit.startDate).format('DD MMM YYYY HH:mm'));

        return startDate > now;
    }

    getSorting() {
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: -1,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: false,
                isHidden: true,
                name: 'startDate',
                title: 'StartDate'
            }
        );
        return columns;
    }

    attached() {
        this.setItemSource();
        this.initializeUow();
    }

    setColumns() {
        const columns = [];

        columns.push(
            {
                columnOrder: 0,
                sortOrder: -1,
                dataType: DataType.DateTime,
                name: 'startDate',
                title: 'Start Date',
                isSortable: true,
                valueConverter: (date: Date | string) => date
                    ? moment(date).format('DD MMM YYYY HH:mm')
                    : ''
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.DateTime,
                name: 'endDate',
                title: 'End Date',
                isSortable: true,
                valueConverter: (date: Date | string) => date
                    ? moment(date).format('DD MMM YYYY HH:mm')
                    : ''
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Default,
                name: 'createdByName',
                title: 'Created By',
                isSortable: true
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Boolean,
                name: 'allowRedirection',
                title: 'Allow Redirection',
                isSortable: true
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.Boolean,
                name: 'isCompleted',
                title: 'Completed',
                isSortable: true
            },
            {
                columnOrder: 0,
                sortOrder: 0,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: "actions",
                isHidden: !this.canComplete && !this.canUpdate && !this.canDelete,
                value: `<div class="actions row justify-content-end align-items-middle">
                            <!-- Complete -->
                            <span if.bind="canComplete() && !item.isCompleted"><i class="icon-complete material-icons" click.trigger="complete(item)">check</i></span>
                            <!-- Edit -->
                            <span if.bind="canUpdate() && !item.isCompleted"><i class="icon-edit material-icons" click.trigger="edit(item)">edit</i></span>
                            <!-- Delete -->
                            <span if.bind="canDelete()"><i class="icon-delete material-icons" click.trigger="delete(item)">delete</i></span>
                        </div>`,
                parameter: {
                    item: null,
                    edit: (item) => {
                        if(item) {
                            this.scheduledMaintenanceService.getScheduledMaintenanceById(item.id).then(x => {
                                if(x)
                                    this.editScheduledMaintenance(x.scheduledMaintenance);
                            });
                        }
                    },
                    delete: (item) => {
                        this.deleteDialog(item.scheduledMaintenanceId);
                    },
                    complete: (item) => {
                        const scheduledMaintenanceToSave: ScheduledMaintenance = item;
                        this.completeDialog(scheduledMaintenanceToSave);
                    },
                    canComplete: () => {
                        return this.canComplete;
                    },
                    canUpdate: () => {
                        return this.canUpdate;
                    },
                    canDelete: () => {
                        return this.canDelete;
                    }
                }
            });

        columns.forEach((column, index) => {
            column.columnOrder = index;
            column.sortOrder = index;
        });

        this.columns = columns;
    }
    
    private setItemSource() { 
        this.itemSource = this.getItemSource();
    }

    private getItemSource() {
        return {
            sourceType: SourceType.ODataUrl,
            source: this.odataUrl,
            columns: this.columns,
            initialFilters: [],
            initialSorting: this.initialSorting
        };
    }

    resetFormData() {
        this.scheduledMaintenanceInEdit = {
            description: '',
            endDate: null,
            startDate: null,
            allowRedirection: false,
            isCompleted: false,
            scheduledMaintenanceId: 0
        };
        this.initializeUow();
    }

    saveScheduledMaintenance(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if(this.scheduledMaintenanceUow && this.scheduledMaintenanceUow.hasChanges()) {

                const changes = this.scheduledMaintenanceUow.getChanges();

                const saveRequest = <SaveScheduledMaintenanceRequest>{
                    changes: changes,
                    id: this.scheduledMaintenanceInEdit.scheduledMaintenanceId
                }

                this.scheduledMaintenanceService.saveScheduledMaintenance(saveRequest)
                    .then(x => {
                        this.setItemSource();
                        this.scheduledMaintenanceInEdit = x.scheduledMaintenance;
                        this.notificationService.show(this.i18N.tr('success-text'), "", NotificationType.Success);
                        this.resetFormData();
                        resolve();
                    })
                    .catch(() => {
                        this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error);
                        reject();
                    });
            } else {
                this.notificationService.show(this.i18N.tr('no-changes-performed-text'), this.i18N.tr('there-were-no-changes-to-save-text'), NotificationType.Info);
                resolve();
            }
        });
    }

    editScheduledMaintenance(scheduledMaintenance: ScheduledMaintenance) {
        if(!scheduledMaintenance)
            return;
        
        this.scheduledMaintenanceInEdit = scheduledMaintenance;
        this.initializeUow();
    }
    
    initializeUow () {
        if (this.scheduledMaintenanceUow)
            this.scheduledMaintenanceUow.dispose();
        this.scheduledMaintenanceUow = UnitOfWorkObserver.getTrackingInstance(this.engine, this.scheduledMaintenanceInEdit);
    }


    forceStartDialog() {
        const model = new GenericConfirmationDialogModel();
        model.confirm = this.i18N.tr('confirm-text');
        model.cancel = this.i18N.tr('cancel-text');
        model.messages.push(this.i18N.tr('are-you-sure-you-want-to-force-start'));
        model.title = this.i18N.tr('confirm-force-start-text');
        model.onConfirm = () => this.forceStart();
        this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
    }

    forceStart() {
        return new Promise<void>((resolve, reject) => {
            if(!this.scheduledMaintenanceInEdit) {
                reject();
                return;
            }
            
            const request = <ForceStartOfScheduledMaintenanceRequest> {
                scheduledMaintenanceId: this.scheduledMaintenanceInEdit.scheduledMaintenanceId
            };
            this.scheduledMaintenanceService.forceStart(request).then(x => {
                if(!x || !x.isSuccess) {
                    this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error);
                    reject();
                    return;
                }
                this.setItemSource();
                this.notificationService.show(this.i18N.tr('success-text'), "", NotificationType.Success);
                this.resetFormData();
                resolve();
            }).catch(() => {
                this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error);
                reject();
            }); 
        });  
    }

    deleteDialog(id: number) {
        const model = new GenericConfirmationDialogModel();
        model.confirm = this.i18N.tr('delete-text');
        model.cancel = this.i18N.tr('cancel-text');
        model.messages.push(this.i18N.tr('are-you-sure-you-want-to-delete'));
        model.title = this.i18N.tr('confirm-delete');
        model.onConfirm = () => this.deleteScheduledMaintenance(id);
        this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
    }

    deleteScheduledMaintenance(id: number): Promise<void>  {
        return new Promise<void>((resolve, reject) => {
                const request = <DeleteScheduledMaintenanceRequest>{
                    scheduledMaintenanceId: id
                };
                this.scheduledMaintenanceService.deleteScheduledMaintenance(request).then(x => {
                if(!x || !x.isSuccess) {
                    this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error);
                    reject();
                    return;
                }
                this.setItemSource();
                if(this.scheduledMaintenanceInEdit && x.scheduledMaintenanceId == this.scheduledMaintenanceInEdit.scheduledMaintenanceId)
                    this.resetFormData();
                
                this.notificationService.show(this.i18N.tr('success-text'), "", NotificationType.Success);
                resolve();
            }).catch(() => {
                this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error);
                reject();
            });
        });
    }

    completeDialog(scheduledMaintenance: ScheduledMaintenance) {
        const model = new GenericConfirmationDialogModel();
        model.confirm = this.i18N.tr('complete-text');
        model.cancel = this.i18N.tr('cancel-text');
        model.messages.push(this.i18N.tr('are-you-sure-you-want-to-complete'));
        model.title = this.i18N.tr('confirm-complete-text');
        model.onConfirm = () => this.complete(scheduledMaintenance);
        this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
    }

    complete(scheduledMaintenance: ScheduledMaintenance): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const request = <CompleteScheduledMaintenanceRequest> {
                scheduledMaintenanceId: scheduledMaintenance.scheduledMaintenanceId
            }
            this.scheduledMaintenanceService.completeScheduledMaintenance(request).then(x => {
                if(!x || !x.isSuccess) {
                    this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error);
                    reject();
                    return;
                }
                this.setItemSource();
                this.notificationService.show(this.i18N.tr('success-text'), "", NotificationType.Success);
                this.resetFormData();
                resolve();
            }).catch(() => {
                this.notificationService.show(this.i18N.tr('error-text'), "", NotificationType.Error);
                reject();
            }); 
        });  
    }

    detached() {
        this.scheduledMaintenanceUow.dispose();
        this.scheduledMaintenanceUow = null;
    }
}
